// The saw records which stick it cut (2026-10-14).
//
// A cut-list task at Fabrication (the saw list, or a lathe list cut from bar: part_id empty,
// cut_list_id set) takes `quantity` sticks out of stock when it completes, and leaves one tail
// remnant per stick. Until now it recorded no lot, so neither the saw-cut parts nor any bar
// remnant could trace its heat. jobq-api now takes the stick's TRK the way the laser takes a
// sheet's (Mike: "As long as we can accurately trace the origin of the remnants and their heat
// number, that's what we're most concerned with"):
//
//   GET  /tasks/{id}/stick_lots   -> the stick material's open lots (candidate_mtns), what this task
//                                    recorded, prefill_mtn_id (exactly one open lot, and the open lots
//                                    account EXACTLY for the sticks in stock), tracking_required (the
//                                    same exact match), untracked_sticks / lots_overstated_sticks
//   POST /tasks/{id}/stick_lots   {pin, lots: [{mtn_id, sticks}], allow_depleted_lot}
//                                 -> replaces the task's rows; "already fully used ... confirm to record
//                                    it anyway" is the confirmable refusal (isConfirmableSheetRefusal)
//
// At completion the tails land in the remnant's own lot from each stick's lot.
import config from "../config.js";

const API_BASE = config.API_BASE_URL;
export const pad6 = (n) => String(n).padStart(6, "0");

/** A cut-list-level Fabrication task: the one that takes sticks out of stock at completion. */
export function isSawStickTask(task) {
  return !!task && task.workstation === "Fabrication" && !task.part_id && !!task.cut_list_id;
}

/** "TRK 000061 · heat HB-ONE · PO 1331 · 3 sticks left" for a lot option. */
export function stickLotLabel(lot) {
  if (!lot) return "";
  const bits = [`TRK ${lot.tracking_number_display || pad6(lot.mtn_id)}`];
  if (lot.heat_number) bits.push(`heat ${lot.heat_number}`);
  if (lot.po_number) bits.push(`PO ${lot.po_number}`);
  else if (lot.customer_name) bits.push(lot.customer_name);
  const n = Number(lot.sticks_remaining || 0);
  bits.push(`${n} stick${n === 1 ? "" : "s"} left`);
  return bits.join(" · ");
}

/** The picker's first rows: what the task already recorded, else the pre-filled lot for every
 *  stick, else one empty row for every stick. */
export function initialStickRows(state) {
  const planned = Math.max(1, Number(state?.sticks_planned || 1));
  const recorded = (state?.recorded || []).filter((r) => r && r.mtn_id);
  if (recorded.length) return recorded.map((r) => ({ mtn: String(r.mtn_id), sticks: String(r.sticks) }));
  return [{ mtn: state?.prefill_mtn_id ? String(state.prefill_mtn_id) : "", sticks: String(planned) }];
}

/** {ok, lots, error}: rows -> the POST body's lots. Each row needs a TRK and at least one stick,
 *  and together they can't be more sticks than the task cuts. */
export function stickRowsToLots(rows, planned) {
  const lots = [];
  for (const r of rows || []) {
    const mtn = parseInt(String(r.mtn || "").replace(/\D/g, ""), 10);
    const sticks = parseInt(String(r.sticks || ""), 10);
    if (!mtn) return { ok: false, error: "Enter the TRK # on the stick's tag for every row." };
    if (!sticks || sticks < 1) return { ok: false, error: "Each lot needs at least 1 stick." };
    lots.push({ mtn_id: mtn, sticks });
  }
  if (!lots.length) return { ok: false, error: "Pick the stick's lot." };
  const total = lots.reduce((s, l) => s + l.sticks, 0);
  if (planned && total > planned) {
    return { ok: false, error: `This task cuts ${planned} stick${planned === 1 ? "" : "s"}; ${total} were entered.` };
  }
  return { ok: true, lots };
}

/** Whether Complete should ask for the stick lot: the task can still take one, has none yet, and
 *  the stick material has a lot to pick from. */
export function shouldAskStickLot(state) {
  if (!state || !state.editable) return false;
  if ((state.recorded || []).some((r) => r && r.mtn_id)) return false;
  return (state.candidate_mtns || []).length > 0;
}

export async function fetchStickLots(taskId) {
  const res = await fetch(`${API_BASE}/tasks/${taskId}/stick_lots`);
  if (!res.ok) return { ok: false, status: res.status, body: null };
  return { ok: true, status: res.status, body: await res.json() };
}

export async function saveStickLots(taskId, { pin, lots, allowDepleted = false }) {
  const res = await fetch(`${API_BASE}/tasks/${taskId}/stick_lots`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pin, lots, allow_depleted_lot: !!allowDepleted }),
  });
  let body = null;
  try { body = await res.json(); } catch { body = null; }
  return { ok: res.ok, status: res.status, body };
}
